import { Card, CardContent, CardHeader, Input, ScrollArea } from '@ibsys/kit';
import { FC, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useHasPermission } from '@/entities/profile';
import { useCreateEmployee, useMergeEmployee, useUpdateEmployee } from '@/entities/employee';
import type { EmployeeListItem } from '@/entities/conversations/model/api/types';
import { getFullName } from '@/shared/lib';
import { SaveButton } from '@/shared/ui';
import { ConfirmationButton } from '@/shared/ui/ConfirmationButton/ConfirmationButton';
import { DeleteEmployeeDialog } from './DeleteEmployeeDialog';

type EmployeeFormValues = {
  firstName: string;
  lastName: string;
  middleName: string;
  externalId: string;
};

type Props = {
  initial?: EmployeeListItem;
};

export const EmployeeForm: FC<Props> = ({ initial }) => {
  const { t } = useTranslation(['common', 'employees']);
  const navigate = useNavigate();
  const canAdmin = useHasPermission('ADMIN_EMPLOYEE');
  const isEdit = !!initial;

  const { control, handleSubmit, formState, reset } = useForm<EmployeeFormValues>({
    defaultValues: {
      firstName: initial?.firstName ?? '',
      lastName: initial?.lastName ?? '',
      middleName: initial?.middleName ?? '',
      externalId: initial?.externalId ?? '',
    },
  });

  const createMutation = useCreateEmployee();
  const updateMutation = useUpdateEmployee();
  const mergeMutation = useMergeEmployee();

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const onSubmit = handleSubmit((values) => {
    const body = {
      firstName: values.firstName,
      lastName: values.lastName,
      middleName: values.middleName || undefined,
      externalId: values.externalId || undefined,
    };

    if (isEdit && initial) {
      updateMutation.mutate(
        { id: initial.id, body },
        {
          onSuccess: () => {
            // Сохранённые значения становятся новой базой — признак изменений сбрасывается,
            // и кнопка «Сохранить» снова гаснет (карточка редактирования не перемонтируется).
            reset(values);
            navigate(`/employees/${initial.id}`);
          },
        },
      );
    } else {
      createMutation.mutate(body, {
        onSuccess: (data) => {
          navigate(`/employees/${data.id}`);
        },
      });
    }
  });

  const handleDelete = useCallback(() => {
    navigate('/employees');
  }, [navigate]);

  const handleMerge = useCallback(() => {
    if (!initial) return;
    mergeMutation.mutate(initial.id);
  }, [initial, mergeMutation]);

  const fullName = useMemo(() => {
    if (!initial) return '';
    return getFullName(initial.firstName, initial.lastName, initial.middleName);
  }, [initial]);

  const titlePlaceholder = isEdit ? t('employees:editingEmployee') : t('employees:creatingEmployee');

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="border-b flex justify-between items-center">
        <span className="text-xl font-semibold">{fullName || titlePlaceholder}</span>
        {canAdmin && (
          <SaveButton
            isDirty={formState.isDirty}
            isLoading={isLoading}
            onClick={onSubmit}
            secondaryAction={
              isEdit && initial && <DeleteEmployeeDialog employeeId={initial.id} onDelete={handleDelete} />
            }
          />
        )}
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden p-0">
        <ScrollArea className="h-full">
          <div className="flex flex-col gap-4 p-6 max-w-2xl">
            <div className="grid grid-cols-2 gap-4 items-start">
              <Input control={control} name="lastName" label={t('employees:lastName')} required disabled={!canAdmin} />
              <Input
                control={control}
                name="firstName"
                label={t('employees:firstName')}
                required
                disabled={!canAdmin}
              />
              <Input control={control} name="middleName" label={t('employees:middleName')} disabled={!canAdmin} />
              <Input control={control} name="externalId" label={t('employees:externalIdLabel')} disabled={!canAdmin} />
            </div>

            {isEdit && canAdmin && (
              <div className="pt-4">
                <ConfirmationButton
                  onConfirm={handleMerge}
                  isLoadingConfirm={mergeMutation.isPending}
                  title={t('employees:mergeTitle')}
                  description={t('employees:mergeDescription', { name: fullName })}
                  trigger={{
                    icon: undefined,
                    variant: 'outline',
                    color: undefined,
                    children: t('employees:mergeButton'),
                  }}
                />
              </div>
            )}
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
};
